/**
 * Data Validation Agent
 * Validates and cleans scraped data, ensures data integrity
 */

interface RawData {
  [key: string]: any;
}

interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  cleanedData?: RawData;
}

interface ValidationError {
  field: string;
  message: string;
  severity: 'critical' | 'high';
  suggestedFix?: string;
}

interface ValidationWarning {
  field: string;
  message: string;
  suggestion?: string;
}

interface DataQualityReport {
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  validityPercentage: number;
  errorsByType: { [key: string]: number };
  warningsByType: { [key: string]: number };
  cleaningSuccessRate: number;
}

class DataValidationAgent {
  /**
   * Validate a single data record
   */
  validateRecord(data: RawData): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];
    let cleanedData = { ...data };

    // Required field validation
    this.validateRequiredFields(data, errors, cleanedData);

    // Type validation
    this.validateDataTypes(data, errors, warnings, cleanedData);

    // Format validation
    this.validateFormats(data, errors, warnings, cleanedData);

    // Range validation
    this.validateRanges(data, errors, warnings, cleanedData);

    // Duplicate/consistency validation
    this.validateConsistency(data, errors, warnings, cleanedData);

    const isValid = errors.filter((e) => e.severity === 'critical').length === 0;

    return {
      isValid,
      errors,
      warnings,
      cleanedData: isValid ? cleanedData : undefined,
    };
  }

  /**
   * Validate required fields
   */
  private validateRequiredFields(
    data: RawData,
    errors: ValidationError[],
    cleanedData: RawData
  ): void {
    const requiredFields = [
      'id',
      'name',
      'location',
      'address',
    ];

    requiredFields.forEach((field) => {
      if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
        errors.push({
          field,
          message: `Required field "${field}" is missing or empty`,
          severity: 'critical',
        });
      }
    });
  }

  /**
   * Validate data types
   */
  private validateDataTypes(
    data: RawData,
    errors: ValidationError[],
    warnings: ValidationWarning[],
    cleanedData: RawData
  ): void {
    // String fields
    const stringFields = ['name', 'address', 'phone', 'email', 'website', 'description'];
    stringFields.forEach((field) => {
      if (data[field] !== undefined && typeof data[field] !== 'string') {
        if (typeof data[field] === 'number') {
          cleanedData[field] = String(data[field]);
          warnings.push({
            field,
            message: `Field "${field}" was not a string - converted to string`,
            suggestion: 'Review the converted value',
          });
        } else {
          errors.push({
            field,
            message: `Field "${field}" must be a string`,
            severity: 'high',
            suggestedFix: 'Convert or remove invalid value',
          });
        }
      }
    });

    // Numeric fields
    const numericFields = ['rating', 'latitude', 'longitude', 'minOrder'];
    numericFields.forEach((field) => {
      if (data[field] !== undefined && typeof data[field] !== 'number') {
        const parsed = parseFloat(data[field]);
        if (!isNaN(parsed)) {
          cleanedData[field] = parsed;
          warnings.push({
            field,
            message: `Field "${field}" was converted to number`,
          });
        } else {
          errors.push({
            field,
            message: `Field "${field}" must be a number`,
            severity: 'high',
          });
        }
      }
    });

    // Array fields
    const arrayFields = ['categories', 'products', 'certifications'];
    arrayFields.forEach((field) => {
      if (data[field] !== undefined && !Array.isArray(data[field])) {
        if (typeof data[field] === 'string') {
          cleanedData[field] = data[field]
            .split(',')
            .map((item: string) => item.trim());
          warnings.push({
            field,
            message: `Field "${field}" was converted from string to array`,
          });
        } else {
          errors.push({
            field,
            message: `Field "${field}" must be an array`,
            severity: 'high',
          });
        }
      }
    });
  }

  /**
   * Validate formats
   */
  private validateFormats(
    data: RawData,
    errors: ValidationError[],
    warnings: ValidationWarning[],
    cleanedData: RawData
  ): void {
    // Email validation
    if (data.email) {
      if (!this.isValidEmail(data.email)) {
        errors.push({
          field: 'email',
          message: 'Email format is invalid',
          severity: 'high',
          suggestedFix: 'Use format: example@domain.com',
        });
      }
    }

    // Phone validation
    if (data.phone) {
      const cleaned = this.cleanPhoneNumber(data.phone);
      if (!this.isValidPhone(cleaned)) {
        errors.push({
          field: 'phone',
          message: 'Phone number format is invalid',
          severity: 'high',
          suggestedFix: 'Use format: (123) 456-7890 or 123-456-7890',
        });
      } else if (cleaned !== data.phone) {
        cleanedData.phone = cleaned;
        warnings.push({
          field: 'phone',
          message: 'Phone number was cleaned',
          suggestion: `Original: ${data.phone}, Cleaned: ${cleaned}`,
        });
      }
    }

    // URL validation
    if (data.website) {
      if (!this.isValidUrl(data.website)) {
        errors.push({
          field: 'website',
          message: 'Website URL format is invalid',
          severity: 'high',
          suggestedFix: 'Use format: https://example.com',
        });
      }
    }

    // ID validation
    if (data.id) {
      if (typeof data.id !== 'string' || data.id.trim().length === 0) {
        errors.push({
          field: 'id',
          message: 'ID must be a non-empty string',
          severity: 'critical',
        });
      }
    }
  }

  /**
   * Validate ranges
   */
  private validateRanges(
    data: RawData,
    errors: ValidationError[],
    warnings: ValidationWarning[],
    cleanedData: RawData
  ): void {
    // Rating range (0-5)
    if (data.rating !== undefined) {
      const rating = typeof data.rating === 'string' ? parseFloat(data.rating) : data.rating;
      if (rating < 0 || rating > 5) {
        errors.push({
          field: 'rating',
          message: 'Rating must be between 0 and 5',
          severity: 'high',
          suggestedFix: 'Adjust rating value or remove',
        });
      }
    }

    // Latitude range (-90 to 90)
    if (data.latitude !== undefined) {
      const lat = typeof data.latitude === 'string' ? parseFloat(data.latitude) : data.latitude;
      if (lat < -90 || lat > 90) {
        errors.push({
          field: 'latitude',
          message: 'Latitude must be between -90 and 90',
          severity: 'high',
        });
      }
    }

    // Longitude range (-180 to 180)
    if (data.longitude !== undefined) {
      const lng = typeof data.longitude === 'string' ? parseFloat(data.longitude) : data.longitude;
      if (lng < -180 || lng > 180) {
        errors.push({
          field: 'longitude',
          message: 'Longitude must be between -180 and 180',
          severity: 'high',
        });
      }
    }

    // String length validation
    if (data.name && typeof data.name === 'string' && data.name.length > 200) {
      warnings.push({
        field: 'name',
        message: 'Name is unusually long (>200 characters)',
        suggestion: 'Consider abbreviating',
      });
    }

    if (data.description && typeof data.description === 'string' && data.description.length > 5000) {
      warnings.push({
        field: 'description',
        message: 'Description is very long (>5000 characters)',
        suggestion: 'Consider condensing',
      });
    }
  }

  /**
   * Validate consistency
   */
  private validateConsistency(
    data: RawData,
    errors: ValidationError[],
    warnings: ValidationWarning[],
    cleanedData: RawData
  ): void {
    // Check for common typos or variations
    if (data.status && typeof data.status === 'string') {
      const status = data.status.toLowerCase();
      if (!['active', 'inactive', 'pending', 'closed'].includes(status)) {
        warnings.push({
          field: 'status',
          message: `Unexpected status value: "${data.status}"`,
          suggestion: 'Use: active, inactive, pending, or closed',
        });
      }
    }

    // Check for empty arrays
    const arrayFields = ['categories', 'products'];
    arrayFields.forEach((field) => {
      if (Array.isArray(data[field]) && data[field].length === 0) {
        warnings.push({
          field,
          message: `Field "${field}" is an empty array`,
          suggestion: 'Add at least one item or remove field',
        });
      }
    });

    // Check for suspicious patterns
    if (data.description && typeof data.description === 'string') {
      if (data.description.toLowerCase().includes('lorem ipsum')) {
        warnings.push({
          field: 'description',
          message: 'Description contains placeholder text',
          suggestion: 'Replace with real description',
        });
      }
    }
  }

  /**
   * Batch validate multiple records
   */
  batchValidate(records: RawData[]): ValidationResult[] {
    return records.map((record) => this.validateRecord(record));
  }

  /**
   * Generate quality report
   */
  generateQualityReport(results: ValidationResult[]): DataQualityReport {
    const totalRecords = results.length;
    const validRecords = results.filter((r) => r.isValid).length;
    const invalidRecords = totalRecords - validRecords;
    const validityPercentage =
      totalRecords > 0 ? (validRecords / totalRecords) * 100 : 0;

    // Count error and warning types
    const errorsByType: { [key: string]: number } = {};
    const warningsByType: { [key: string]: number } = {};

    results.forEach((result) => {
      result.errors.forEach((error) => {
        errorsByType[error.field] = (errorsByType[error.field] || 0) + 1;
      });

      result.warnings.forEach((warning) => {
        warningsByType[warning.field] = (warningsByType[warning.field] || 0) + 1;
      });
    });

    // Calculate cleaning success rate
    const cleanedRecords = results.filter(
      (r) => r.cleanedData !== undefined
    ).length;
    const cleaningSuccessRate =
      totalRecords > 0 ? (cleanedRecords / totalRecords) * 100 : 0;

    return {
      totalRecords,
      validRecords,
      invalidRecords,
      validityPercentage: Math.round(validityPercentage * 100) / 100,
      errorsByType,
      warningsByType,
      cleaningSuccessRate: Math.round(cleaningSuccessRate * 100) / 100,
    };
  }

  /**
   * Clean dataset
   */
  cleanDataset(records: RawData[]): {
    cleaned: RawData[];
    report: DataQualityReport;
    removedRecords: number;
  } {
    const results = this.batchValidate(records);
    const cleaned = results
      .filter((r) => r.cleanedData !== undefined)
      .map((r) => r.cleanedData!);

    const report = this.generateQualityReport(results);
    const removedRecords = records.length - cleaned.length;

    return {
      cleaned,
      report,
      removedRecords,
    };
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Clean phone number
   */
  private cleanPhoneNumber(phone: string): string {
    return phone.replace(/\D/g, '').slice(-10);
  }

  /**
   * Validate phone format
   */
  private isValidPhone(phone: string): boolean {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10;
  }

  /**
   * Validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate CSV/JSON data
   */
  validateImportedData(data: string, format: 'csv' | 'json'): {
    success: boolean;
    records: RawData[];
    errors: string[];
  } {
    const errors: string[] = [];
    let records: RawData[] = [];

    try {
      if (format === 'json') {
        const parsed = JSON.parse(data);
        records = Array.isArray(parsed) ? parsed : [parsed];
      } else if (format === 'csv') {
        records = this.parseCSV(data);
      }
    } catch (error) {
      errors.push(
        `Failed to parse ${format.toUpperCase()}: ${error instanceof Error ? error.message : String(error)}`
      );
      return { success: false, records: [], errors };
    }

    if (records.length === 0) {
      errors.push('No records found in imported data');
      return { success: false, records, errors };
    }

    return {
      success: errors.length === 0,
      records,
      errors,
    };
  }

  /**
   * Parse CSV data
   */
  private parseCSV(csvData: string): RawData[] {
    const lines = csvData.split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim());
    const records: RawData[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim());
      if (values.length === headers.length) {
        const record: RawData = {};
        headers.forEach((header, index) => {
          record[header] = values[index];
        });
        records.push(record);
      }
    }

    return records;
  }
}

export default new DataValidationAgent();
